<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import type { ApiError } from '@/api/client';
import { gameNotOpenMessage, statusFrom, userMessage } from '@/api/errors';
import { formatMoney, gridSummary, squareKey, GRID_SIZE } from '@/api/grid';
import { holdingsLabel, playerWinnerSentence, policyForPlayers } from '@/api/lifecycle';
import { conflictsFrom, remainingAllowance, selectionCost, suggestedAutoCount } from '@/api/picks';
import { isPredicting, predictedSquare } from '@/api/prediction';
import GameTimestamps from '@/components/GameTimestamps.vue';
import ShareGame from '@/components/ShareGame.vue';
import SquaresGrid from '@/components/SquaresGrid.vue';
import { useSquareTaps } from '@/composables/useSquareTaps';
import { t } from '@/i18n';
import type { Coordinate } from '@/api/types';
import PollingIdleBanner from '@/components/PollingIdleBanner.vue';
import GameLoadError from '@/components/GameLoadError.vue';
import { useGameScreen } from '@/composables/useGameScreen';

/*
 * The grid. docs/UX_FLOWS.md is the specification — a tap is the claim, a second tap gives the
 * square back, and everything a player can *do* sits above the board.
 */

const props = defineProps<{ code: string }>();

/**
 * Which sentence a failure becomes on *this* screen — the one thing `useGameScreen` cannot decide.
 *
 * Three codes earn their own words here. A square somebody else took has to be *named*, not
 * counted; a game that is no longer open should say which of locked, completed or cancelled it
 * is; and an auto-pick that could not find room should say how much room there was.
 */
function playSentenceFor(error: ApiError): string {
  if (error.code === 'SQUARE_TAKEN') {
    const conflicts = conflictsFrom(error.details);
    // Named, not just counted: "someone took one of your squares" leaves the player hunting.
    const named = (conflicts ?? []).map((c) => squareKey(c.col, c.row)).join(', ');
    return named
      ? t('game.somebodyGotThereFirst', { keys: named })
      : userMessage(error.code, error.message);
  }

  // The status is in the details, so the sentence can name it rather than being vague.
  if (error.code === 'GAME_NOT_OPEN') return gameNotOpenMessage(statusFrom(error.details));

  if (error.code === 'NOT_ENOUGH_SQUARES') {
    const available = (error.details as { available?: number } | undefined)?.available;
    return available === undefined
      ? userMessage(error.code, error.message)
      : t('game.onlyLeft', { n: available }, available);
  }

  return userMessage(error.code, error.message);
}

/*
 * The screen's shared half — see `useGameScreen`.
 *
 * `forMoney` is worth naming here even though it is shared: a price of zero is a game played for
 * a shot, and every amount, the pot and the whole paid column come off this screen behind it.
 */
const {
  games,
  session,
  message,
  detail,
  game,
  isOpen,
  isCancelled,
  isHost,
  forMoney,
  mySubjectId,
  load,
  feed,
  watching,
  boardNews,
  handleFailure,
  run,
} = useGameScreen(() => props.code, { sentenceFor: playSentenceFor });

/** Null until they join — a square carries the name they joined *this game* under. */
const me = computed(
  () => detail.value?.players.find((player) => player.subjectId === mySubjectId.value) ?? null,
);
const hasJoined = computed(() => me.value !== null);

const mySquares = computed(() =>
  (detail.value?.squares ?? []).filter((square) => square.subjectId === mySubjectId.value),
);

/*
 * Who else is in, in the order they joined. Deliberately not sorted by holdings: this screen
 * re-reads every ten seconds while a pool fills, and a list that reorders itself under somebody
 * reading it is worse than one whose biggest holder is not first.
 */
const others = computed(() =>
  (detail.value?.players ?? []).filter((player) => player.subjectId !== mySubjectId.value),
);

/** The result in this reader's own terms — "you won", or which of the two empty-square endings. */
const resultSentence = computed(() =>
  game.value?.winner
    ? playerWinnerSentence(
        game.value.winner,
        game.value.currency,
        mySubjectId.value,
        forMoney.value,
      )
    : null,
);

/** What the host chose for the empty squares, once they have chosen. Null while OPEN. */
const emptySquareRule = computed(() =>
  policyForPlayers(game.value?.emptySquarePolicy ?? null, forMoney.value),
);

/*
 * Counted off the optimistic board rather than the server's, so a burst of taps cannot walk past
 * the per-player limit while the first of them is still in the air.
 */
const takenSquares = computed(() => shownSquares.value.length);
const freeSquares = computed(() => GRID_SIZE * GRID_SIZE - takenSquares.value);
const heldByMe = computed(
  () => shownSquares.value.filter((square) => square.subjectId === mySubjectId.value).length,
);

const allowance = computed(() =>
  game.value
    ? remainingAllowance({
        maxSquaresPerPlayer: game.value.maxSquaresPerPlayer ?? 0,
        alreadyHeld: heldByMe.value,
        freeSquares: freeSquares.value,
      })
    : 0,
);

const autoCount = computed(() =>
  game.value
    ? suggestedAutoCount(
        {
          recommendedSquaresPerPlayer: game.value.recommendedSquaresPerPlayer,
          maxSquaresPerPlayer: game.value.maxSquaresPerPlayer ?? 0,
        },
        { alreadyHeld: heldByMe.value, freeSquares: freeSquares.value },
      )
    : 0,
);

/**
 * Whether the board answers a tap at all. Not gated on the allowance: somebody at their limit
 * must still be able to tap one of their own squares to give it back, and a tap on an empty one
 * is answered with the reason rather than with nothing happening.
 */
const canTap = computed(() => isOpen.value && hasJoined.value);
const canPick = computed(() => canTap.value && allowance.value > 0);

const money = (amount: number): string => formatMoney(amount, game.value?.currency ?? '');

/**
 * Revealed only once a score exists, and taken from the API rather than worked out here: the
 * backend resolves the winner against the game's stored digit axes, and a second implementation
 * on this screen would be free to disagree with the one that decides who is owed money.
 */
const winning = computed(() => game.value?.winner?.winningSquare ?? null);

/*
 * The predicted square, and whether to say the answer is still coming. Both derived rather than
 * held: the poll replaces the game object, and the prediction settles in the middle of a game's
 * life rather than at load.
 */
const predicted = computed(() => predictedSquare(game.value));
const predicting = computed(() => isPredicting(game.value));

/*
 * A tap is the claim; a tap on one of your own gives it back. The optimism, the serialising and
 * the re-read live in useSquareTaps, because the host's manage screen takes picks on the same
 * terms — see docs/UX_FLOWS.md.
 */
const { shownSquares, onToggle: tap } = useSquareTaps({
  squares: computed(() => detail.value?.squares ?? []),
  subjectId: mySubjectId,
  // The name they joined *this* game under, which is what the grid renders.
  displayName: computed(() => me.value?.displayName ?? session.subject?.displayName ?? ''),
  allowance,
  claim: async (square) => {
    try {
      await games.claim(props.code, [square]);
    } catch (caught) {
      handleFailure(caught);
    }
  },
  release: async (square) => {
    try {
      await games.release(props.code, square);
    } catch (caught) {
      handleFailure(caught);
    }
  },
  // An arrow, not the reference: this is set up above where `load` is declared.
  reload: () => load(),
  onBlocked: () => {
    message.value = t('game.allowanceReached', { max: game.value?.maxSquaresPerPlayer ?? 0 });
  },
});

/** Clearing the last complaint is this screen's job, not the composable's. */
function onToggle(square: Coordinate): void {
  message.value = null;
  tap(square);
}

async function pickForMe(): Promise<void> {
  if (autoCount.value === 0) return;
  // `run` owns the rest: the saving guard, clearing the last complaint, and re-reading whether it
  // worked or not.
  await run(() => games.autoPick(props.code, autoCount.value));
}
</script>

<template>
  <section>
    <div v-if="games.loading && !game" aria-busy="true">
      <h1 class="text-2xl font-semibold">{{ t('game.loading') }}</h1>
      <div class="mt-6 h-64 animate-pulse rounded-lg border border-line bg-surface" />
    </div>

    <GameLoadError v-else-if="games.error" :error="games.error" @retry="load" />

    <div v-else-if="game">
      <div class="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h1 class="text-2xl font-semibold">{{ game.name }}</h1>
          <!-- Two forms of one line: a game with no price has no "a square" clause to end on. -->
          <p class="mt-1 text-sm text-ink-muted">
            {{
              t(forMoney ? 'game.axes' : 'game.axesFree', {
                teamA: game.teamA,
                teamB: game.teamB,
                price: money(game.pricePerSquare),
              })
            }}
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <!-- The escape hatch for anyone who does not want to wait for the next read, and the
               only control here that fetches the game rather than the page. -->
          <button
            v-if="watching"
            type="button"
            :disabled="feed.refreshing.value"
            :aria-busy="feed.refreshing.value ? 'true' : undefined"
            class="rounded border border-accent px-3 py-1.5 text-sm font-medium hover:bg-fill disabled:border-line disabled:bg-surface-sunken disabled:text-ink-muted"
            @click="feed.refreshNow()"
          >
            {{ t('game.refresh') }}
          </button>
          <RouterLink
            v-if="isHost"
            :to="`/host/${game.code}`"
            class="rounded border border-accent px-3 py-1.5 text-sm font-medium hover:bg-fill"
          >
            {{ t('game.manage') }}
          </RouterLink>
        </div>
      </div>

      <!-- How full the grid is survives a game with no money; the pot beside it does not. -->
      <p class="mt-3 text-sm text-ink-muted">
        {{ gridSummary(takenSquares, game.squaresClaimed ?? 0) }}
        <template v-if="forMoney">
          · {{ t('game.pot') }} <strong>{{ money(game.pot ?? 0) }}</strong>
        </template>
      </p>

      <!--
        The players' half of `allowHostSuperPowers`. Said either way, and said here rather than
        buried below the grid: a host who can put your name on a square you never asked for is
        something to know *before* you pick, not after. See docs/GAME_RULES.md.
      -->
      <p class="mt-1 text-sm text-ink-muted">
        {{ (game.allowHostSuperPowers ?? true) ? t('game.powersOn') : t('game.powersOff') }}
      </p>

      <!-- A cancelled game must not show a grid nobody can use. A completed one must: the
           winning square is the whole payoff. See docs/UX_FLOWS.md. -->
      <div v-if="isCancelled" class="mt-6 rounded-lg border border-line bg-surface p-4">
        <h2 class="font-semibold">{{ t('game.cancelledHeading') }}</h2>
        <p class="mt-1 text-sm text-ink-muted">{{ t('game.cancelledBody') }}</p>
      </div>

      <template v-else>
        <!-- Above the grid, where the final score goes: it is the first thing to read, because
             everything below it may be an hour out of date. -->
        <!--
        What a poll changed, for somebody who cannot see it change. `sr-only` because the board
        itself is the visible version of this sentence, and `aria-live="polite"` because it is
        worth hearing at the next pause rather than interrupting for. See `boardNews`.
      -->
        <p data-board-news aria-live="polite" class="sr-only">{{ boardNews }}</p>

        <PollingIdleBanner
          :idle="feed.idle.value"
          :refreshing="feed.refreshing.value"
          @refresh="feed.refreshNow()"
        />

        <div v-if="game.finalScore" class="mt-4 rounded-lg bg-win/20 p-3 text-sm">
          <p class="font-medium">
            {{
              t('game.finalScore', {
                teamA: game.teamA,
                scoreA: game.finalScore.teamA,
                teamB: game.teamB,
                scoreB: game.finalScore.teamB,
              })
            }}
          </p>
          <!-- Who actually won, in words. A ringed square nobody holds reads the same whether the
               host collects or nobody does, and those are opposite outcomes. -->
          <p v-if="resultSentence" class="mt-1 font-medium">{{ resultSentence }}</p>
        </div>
        <div v-else-if="!isOpen" class="mt-4 text-sm">
          <!-- The same sentence a 409 GAME_NOT_OPEN produces, from the same key. -->
          <p class="font-medium text-ink">{{ t('errors.notOpen.LOCKED') }}</p>
          <!-- The empty-square rule is chosen at lock time, so this is the first moment anybody
               but the host could be told what it is. -->
          <p v-if="emptySquareRule" class="mt-1 text-ink-muted">{{ emptySquareRule }}</p>
        </div>

        <!--
          Everything a player can *do* goes above the board, and everything that merely reports
          goes below it. The grid is about as tall as a phone, so anything under it is off the
          screen — which is how people ended up picking squares and never finding the button that
          claimed them. See docs/UX_FLOWS.md.
        -->
        <!-- Not joined: asked to, rather than shown controls that would answer NOT_JOINED. -->
        <div v-if="isOpen && !hasJoined" class="mt-4 rounded-lg border border-line bg-surface p-4">
          <h2 class="font-semibold">{{ t('game.wantInHeading') }}</h2>
          <p class="mt-1 text-sm text-ink-muted">{{ t('game.wantInBody') }}</p>
          <RouterLink
            :to="`/join/${game.code}`"
            class="mt-3 inline-block rounded bg-accent px-4 py-2 font-medium text-on-accent hover:bg-accent-strong"
          >
            {{ t('game.joinThisGame') }}
          </RouterLink>
        </div>

        <div v-else-if="canPick" class="mt-4 rounded-lg border border-line bg-surface p-4">
          <!-- "Pick for me" first and pre-set to the host's recommendation: most people do not
               have a favourite square, they want in. -->
          <div class="flex flex-wrap items-center gap-3">
            <button
              v-if="autoCount > 0"
              type="button"
              :disabled="games.saving"
              class="rounded bg-accent px-4 py-2 font-medium text-on-accent hover:bg-accent-strong disabled:border-line disabled:bg-surface-sunken disabled:text-ink-muted"
              @click="pickForMe"
            >
              {{
                games.saving
                  ? t('game.picking')
                  : t(forMoney ? 'game.pickForMe' : 'game.pickForMeFree', {
                      n: autoCount,
                      amount: money(selectionCost(autoCount, game.pricePerSquare)),
                    })
              }}
            </button>
            <p class="text-sm text-ink-muted">{{ t('game.tapToTake', { n: allowance }) }}</p>
          </div>
        </div>

        <p v-else-if="canTap" class="mt-4 text-sm font-medium text-ink">
          {{ t('game.atLimit', { max: game.maxSquaresPerPlayer }) }}
        </p>

        <!-- Directly above the board, not at the foot of the screen: on a phone the grid is the
             fold, and a conflict nobody scrolls to see is a conflict nobody hears about. -->
        <p aria-live="polite" class="mt-3 min-h-6 text-sm font-medium text-ink">
          {{ message }}
        </p>

        <div class="mt-2 rounded-lg border border-line bg-surface p-4">
          <!-- Framed like the landing page's board: a card that lifts the grid off the page rather
             than leaving it floating in it. The measurements are *not* the landing page's — no
             `max-w-3xl`, and the grid keeps its 420px floor — because that board is a picture to
             watch and this one is a thing to hit with a thumb. -->
          <SquaresGrid
            :squares="shownSquares"
            :team-a="game.teamA"
            :team-b="game.teamB"
            :team-a-colour="game.teamAColour"
            :team-b-colour="game.teamBColour"
            :column-digits="game.columnDigits"
            :row-digits="game.rowDigits"
            :winning="winning"
            :predicted="predicted"
            :predicting="predicting"
            :interactive="canTap"
            :owner-id="mySubjectId"
            @toggle="onToggle"
          />
        </div>

        <!-- Getting somebody else in. Below the picking and above the ledger, because that is
             the order somebody works through this screen: my squares first, then who else should
             be in. Only while OPEN — nobody can join a locked game, so an invitation then would
             be one to a closed door. -->
        <ShareGame v-if="isOpen" :code="game.code" :name="game.name" class="mt-6">
          {{ t('game.shareNote') }}
        </ShareGame>

        <!-- What they hold and what it costs. No list of squares and no Release buttons: the
             board above is the list, in colour, and a tap on one of them gives it back. -->
        <div
          v-if="mySquares.length > 0 || others.length > 0"
          class="mt-6 rounded-lg border border-line bg-surface p-4"
        >
          <template v-if="mySquares.length > 0">
            <h2 class="font-semibold">
              {{
                t(
                  forMoney ? 'game.yourSquares' : 'game.yourSquaresFree',
                  { n: mySquares.length, amount: money(me?.amountOwed ?? 0) },
                  mySquares.length,
                )
              }}
            </h2>
            <!-- The word, not a colour, and labelled so a screen reader can find it: this is the
                 line a player comes back to the screen for. Gone entirely with no money in the
                 game — "Not paid yet" reads as a debt to somebody who owes nothing. -->
            <p
              v-if="forMoney"
              :aria-label="t('game.paymentLabel')"
              class="mt-1 text-sm font-medium"
            >
              {{ me?.paid ? t('game.paid') : t('game.notPaid') }}
            </p>
          </template>

          <!-- Everybody else, same three facts as the host's ledger. The grid already names them,
               so what is new here is the money — and in a friends' pool, who has not paid yet is
               exactly what stops the host having to chase people one at a time. -->
          <template v-if="others.length > 0">
            <h3 :class="mySquares.length > 0 ? 'mt-4 font-semibold' : 'font-semibold'">
              {{ t('game.otherPlayers') }}
            </h3>
            <ul class="mt-2 divide-y divide-line">
              <li
                v-for="player in others"
                :key="player.subjectId"
                class="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
              >
                <span>
                  <span class="font-medium">{{ player.displayName }}</span>
                  <span class="text-ink-muted"> · {{ holdingsLabel(player) }}</span>
                </span>
                <span v-if="forMoney" class="flex items-center gap-3">
                  <span class="font-medium">{{ money(player.amountOwed) }}</span>
                  <span :class="player.paid ? 'text-ink-muted' : 'font-medium text-ink'">
                    {{ player.paid ? t('game.paidShort') : t('game.notPaidShort') }}
                  </span>
                </span>
              </li>
            </ul>
          </template>
        </div>
      </template>

      <!-- Last, and quiet: provenance rather than something anybody acts on. -->
      <GameTimestamps :game="game" class="mt-6" />
    </div>
  </section>
</template>
